import { useEffect, useState } from "react";
import type { BuildEvent, Job } from "../../lib/api";
import { elapsedLabel } from "./buildProgress";
import "./buildWaiting.css";

const STAGES = {
  queued: { step: 0, title: "Getting your build ready", detail: "Your build is queued and will start automatically. There’s nothing else you need to do." },
  learning: { step: 0, title: "Getting to know your business", detail: "Gathering your brief, photographs and inspiration to give the design a place to start." },
  preparing: { step: 0, title: "Preparing your first draft", detail: "Organizing your business details and preparing the content for your website." },
  working: { step: -1, title: "Working on your website", detail: "Your build is running. We’ll show the next confirmed stage here." },
  exploring: { step: 1, title: "Exploring the possibilities", detail: "Turning your brief and creative direction into real page designs." },
  comparing: { step: 1, title: "Finding the strongest direction", detail: "Comparing the designs on desktop and phone before choosing one to develop." },
  composing: { step: 2, title: "Bringing your pages together", detail: "Building out the content and details in one consistent design." },
  refining: { step: 3, title: "Giving it a closer look", detail: "Reviewing the rendered pages and refining the details that make the design feel finished." },
  reviewing: { step: 3, title: "Reviewing your pages", detail: "Reviewing the content and checking the details across your pages." },
  checking: { step: 3, title: "Checking the finishing touches", detail: "Checking the layouts, navigation and links before your preview is ready." },
  publishing: { step: 4, title: "Preparing your preview", detail: "Putting the pages online and checking that your preview opens." },
  assets: { step: 1, title: "Preparing the images", detail: "Gathering and preparing the images chosen for your pages." },
  delivery: { step: 4, title: "Your pages are built. Connecting the preview…", detail: "The finished draft is saved. We’re retrying the preview connection using those same pages." },
} as const;

type Stage = keyof typeof STAGES;

/** Only canonical status and explicit runner milestones advance the UI. Logs and time do not. */
export function buildStage(status: Job["status"], events: BuildEvent[], executionStage?: string): Stage {
  if (status === "delivery_pending") return "delivery";
  if (["shipping", "publishing"].includes(status)) return "publishing";
  if (["queued", "retrying"].includes(status)) return "queued";
  if (executionStage) {
    const stages: Record<string, Stage> = {
      "agent-source": "working", "agent.read_file": "working", "agent.list_files": "working",
      "agent.inspect_reference": "exploring", "agent.prepare_draft_content": "preparing",
      "agent.search_photography": "assets", "agent.select_photograph": "assets", "agent.view_asset": "assets",
      "agent.write_files": "composing", "agent.delegate": "composing", "agent.read_proposal": "reviewing", "agent.apply_proposal": "refining",
      "agent.compile_site": "checking", "agent.inspect_page": "reviewing", "agent.review_content": "reviewing", "agent.verify_site": "checking", "agent.finish": "checking",
      materials: "learning", material: "learning", snapshot: "learning", "draft-materials": "preparing",
      creative: "exploring", creative_spec: "exploring", design: "exploring", assets: "assets", "photography-plan": "assets",
      shell: "composing", modules: "composing", authoring: "composing", "edited-source": "composing",
      "authored-source": "reviewing", "content-review": "reviewing", "content-completed-source": "reviewing",
      "draft-source": "checking", "css-syntax-check": "checking", "css-syntax-plan": "checking", "syntax-completed-source": "checking",
      "render-draft-source": "checking", "draft-verification": "checking", "css-correction": "refining",
      "correction-verification": "checking", source: "checking",
      compile: "checking", verification: "checking", verify: "checking", publication: "publishing", delivery: "delivery",
    };
    if (Object.prototype.hasOwnProperty.call(stages, executionStage)) return stages[executionStage];
    if (/^(?:asset|stock-pool|stock-choice)\.[a-z0-9_-]+$/i.test(executionStage)) return "assets";
    if (/^page\.[a-z0-9_-]+$/i.test(executionStage)) return "composing";
    // An explicit but unfamiliar Studio stage cannot inherit later legacy log
    // milestones or mark earlier phases complete without confirmation.
    return "working";
  }
  if (status === "validating") return "checking";
  if (status === "grounding") return "learning";
  for (const event of [...events].reverse()) {
    const data = event.data;
    if (!data || typeof data !== "object" || !("stage" in data)) continue;
    if (typeof data.stage === "string" && Object.prototype.hasOwnProperty.call(STAGES, data.stage)) return data.stage as Stage;
  }
  return ["extracting_inspo", "synthesizing", "scaffolding"].includes(status) ? "exploring" : "composing";
}

export function BuildWaiting({ status, events, reconnecting = false, job }: {
  status: Job["status"]; events: BuildEvent[]; reconnecting?: boolean; job?: Job | null;
}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  const stage = buildStage(status, events, job?.execution?.stage);
  const current = STAGES[stage];
  const elapsed = elapsedLabel(job?.execution?.queuedAt ?? job?.createdAt, now);
  const queuedFor = stage === "queued" ? elapsedLabel(job?.execution?.queuedAt ?? job?.createdAt, now) : null;
  const previewUrl = job?.verifiedPreviewUrl && /^https:\/\//.test(job.verifiedPreviewUrl) ? job.verifiedPreviewUrl : null;
  return <section className="build-studio" aria-label="Website build progress">
    <div className="build-studio-intro">
      <span className="build-studio-state"><i aria-hidden="true" /> Work in progress</span>
      <h1>Your website is<br />taking shape.</h1>
      <p>You can leave this page and come back.<br />Your progress stays with your site.</p>
    </div>
    <div className="build-studio-art" aria-hidden="true">
      <div className="studio-paper studio-paper-back"><div /><i /><i /><i /></div>
      <div className="studio-paper studio-paper-front">
        <div className="studio-paper-nav"><b /><span /><span /></div>
        <div className="studio-paper-layout"><div className="studio-paper-type"><b /><b /><i /></div><div className="studio-paper-image"><i /></div></div>
        <div className="studio-paper-foot"><i /><i /><i /></div>
      </div>
      <div className="studio-swatches"><i /><i /><i /><i /></div>
      <span className="studio-art-caption">Ideas, coming together.</span>
    </div>
    <div className="build-studio-current" role="status" aria-live="polite" aria-atomic="true">
      <span className="studio-stage-mark" aria-hidden="true"><i /></span>
      <div><h2>{current.title}</h2><p>{current.detail}</p></div>
    </div>
    <ol className="build-studio-steps" aria-label="Build stages">
      {["Your brief", "Design", "Pages", "Checks", "Preview"].map((label, index) => <li
        key={label} className={index < current.step ? "complete" : index === current.step ? "active" : ""}
        aria-current={index === current.step ? "step" : undefined}>
        <span aria-hidden="true">{index < current.step ? "✓" : ""}</span>{label}
        {index < current.step && <span className="studio-sr-only"> complete</span>}
      </li>)}
    </ol>
    {elapsed && <p className="build-studio-time">{queuedFor ? `In the queue for ${queuedFor}` : `Elapsed ${elapsed}`}</p>}
    {previewUrl && <a className="btn build-studio-preview" href={previewUrl} target="_blank" rel="noopener noreferrer">Open the available preview</a>}
    {reconnecting && <p className="build-studio-reconnect" role="status">We’re reconnecting to your build. Your last confirmed stage is shown above.</p>}
    <p className="build-studio-away">Feel free to step away. Your build keeps running,<br className="studio-desktop-break" /> and you can find it again from your sites.</p>
  </section>;
}
